// The cached server records of one offline-store scope (BL-2340): auctions, lots (parented by
// auction) and reference data, read only while offline (ADR 066 — Offline Mode § 6.1 limit 4).
import type { OfflineCollection, OfflineEntrySize, OfflineRecordInput, OfflineRecordMap, OfflineRecords } from './types';
import type { OfflineStoreKey, OfflineStoreWrite } from './backend';
import {
  childEntries,
  childKeys,
  chunks,
  dropChildrenWrites,
  lastPerKey,
  placementWrites,
  type ScopeContext,
} from './parent-index';

/** What `records` holds. */
interface RecordRow {
  parentKey: string | null;
  value: unknown;
}

const encoder = new TextEncoder();

/**
 * @param value - a record.
 * @returns its size as UTF-8 JSON, the measure an auction's storage size is counted in (BL-2344).
 */
export function offlineRecordBytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).byteLength;
}

function recordKeyOf(ctx: ScopeContext, collection: OfflineCollection, recordKey: string): OfflineStoreKey {
  return [ctx.scope.tenantId, ctx.scope.userId, collection, recordKey];
}

/**
 * The writes that store records whole, with their parent index entries, for a caller to commit in
 * its own batch (the change queue's local write, BL-2431). Keys must be unique within `items`.
 *
 * @param input - the scope context, the collection and the records.
 * @returns the writes.
 */
export async function recordPutWrites<C extends OfflineCollection>(input: {
  ctx: ScopeContext;
  collection: C;
  items: OfflineRecordInput<C>[];
}): Promise<OfflineStoreWrite[]> {
  const { ctx, collection, items } = input;
  const index = await placementWrites({
    ctx,
    kind: collection,
    // A parented record carries its size on its index entry, so a parent's size is an index read.
    placements: items.map((item) => ({
      childKey: item.key,
      parentKey: item.parentKey ?? null,
      bytes: item.parentKey == null ? undefined : offlineRecordBytes(item.value),
    })),
  });
  const rows = items.map(
    (item): OfflineStoreWrite => ({
      type: 'put',
      table: 'records',
      key: recordKeyOf(ctx, collection, item.key),
      value: { parentKey: item.parentKey ?? null, value: item.value },
    }),
  );
  return [...rows, ...index];
}

/**
 * The writes that remove records and their parent index entries, for a caller's own batch.
 *
 * @param input - the scope context, the collection and the record keys (unique).
 * @returns the writes.
 */
export async function recordDeleteWrites(input: {
  ctx: ScopeContext;
  collection: OfflineCollection;
  keys: string[];
}): Promise<OfflineStoreWrite[]> {
  const { ctx, collection, keys } = input;
  const index = await placementWrites({ ctx, kind: collection, placements: keys.map((childKey) => ({ childKey, parentKey: null })) });
  const deletes = keys.map((k): OfflineStoreWrite => ({ type: 'delete', table: 'records', key: recordKeyOf(ctx, collection, k) }));
  return [...deletes, ...index];
}

/**
 * @param ctx - the scope context.
 * @returns the scope's record collections.
 */
export function createRecords(ctx: ScopeContext): OfflineRecords {
  const { backend, live } = ctx;
  const { tenantId, userId } = ctx.scope;
  const key = (collection: OfflineCollection, recordKey: string): OfflineStoreKey => [tenantId, userId, collection, recordKey];

  const writeChunk = async <C extends OfflineCollection>(collection: C, chunk: OfflineRecordInput<C>[]) => {
    await backend.write(await recordPutWrites({ ctx, collection, items: chunk }), { durability: 'relaxed' });
  };

  const putMany: OfflineRecords['putMany'] = async ({ collection, items }) => {
    // One serial slot per chunk, so a queued edit is not held behind a whole sync.
    for (const chunk of chunks(lastPerKey(items, (item) => item.key))) {
      await live(() => writeChunk(collection, chunk));
    }
  };

  const getMany = async <C extends OfflineCollection>(collection: C, recordKeys: string[]) => {
    const rows = (await backend.getMany(
      'records',
      recordKeys.map((k) => key(collection, k)),
    )) as (RecordRow | undefined)[];
    return rows.map((row) => (row?.value as OfflineRecordMap[C] | undefined) ?? null);
  };

  const { deleteMany, entriesByParent } = createBulkRecordOps({ ctx, key, getMany });

  return {
    get: ({ collection, key: recordKey }) => live(async () => (await getMany(collection, [recordKey]))[0]),
    getMany: ({ collection, keys }) => live(() => getMany(collection, keys)),
    put: ({ collection, key: recordKey, value, parentKey = null }) =>
      putMany({ collection, items: [{ key: recordKey, value, parentKey }] }),
    putMany,
    delete: ({ collection, key: recordKey }) =>
      live(async () => {
        if ((await backend.scanKeys('records', key(collection, recordKey))).length === 0) return false;
        await backend.write(await recordDeleteWrites({ ctx, collection, keys: [recordKey] }), { durability: 'relaxed' });
        return true;
      }),
    list: <C extends OfflineCollection>({ collection, parentKey }: { collection: C; parentKey?: string }) =>
      live(async () => {
        if (parentKey === undefined) {
          const rows = await backend.scan('records', [tenantId, userId, collection]);
          return rows.map(({ value }) => (value as RecordRow).value as OfflineRecordMap[C]);
        }
        const values = await getMany(collection, await childKeys({ ctx, kind: collection, parentKey }));
        return values.filter((value): value is OfflineRecordMap[C] => value !== null);
      }),
    deleteByParent: ({ collection, parentKey }) =>
      live(async () => {
        const children = await childKeys({ ctx, kind: collection, parentKey });
        const writes = dropChildrenWrites({
          ctx,
          kind: collection,
          table: 'records',
          parentKey,
          children,
          rowKey: (child) => key(collection, child),
        });
        await backend.write(writes, { durability: 'relaxed' });
        return children.length;
      }),
    deleteMany,
    entriesByParent,
  };
}

/**
 * The batch delete and the size listing (BL-2429), kept apart from {@link createRecords} so each
 * function stays short.
 *
 * @param input - the scope context, the row key builder and the unscheduled bulk read.
 * @returns `deleteMany` and `entriesByParent`.
 */
function createBulkRecordOps(input: {
  ctx: ScopeContext;
  key: (collection: OfflineCollection, recordKey: string) => OfflineStoreKey;
  getMany: (collection: OfflineCollection, recordKeys: string[]) => Promise<unknown[]>;
}): Pick<OfflineRecords, 'deleteMany' | 'entriesByParent'> {
  const { ctx, key, getMany } = input;
  const { backend, live } = ctx;
  const deleteMany: OfflineRecords['deleteMany'] = ({ collection, keys }) =>
    live(async () => {
      const unique = [...new Set(keys)];
      const rows = (await backend.getMany('records', unique.map((k) => key(collection, k)))) as (RecordRow | undefined)[];
      const present = unique.filter((_, i) => rows[i] !== undefined);
      if (present.length === 0) return 0;
      await backend.write(await recordDeleteWrites({ ctx, collection, keys: present }), { durability: 'relaxed' });
      return present.length;
    });

  const entriesByParent: OfflineRecords['entriesByParent'] = ({ collection, parentKey }) =>
    live(async (): Promise<OfflineEntrySize[]> => {
      const entries = await childEntries({ ctx, kind: collection, parentKey });
      // An entry from before BL-2429 carries no size: measure that record once, from the row.
      const unsized = entries.filter((entry) => entry.bytes === null).map((entry) => entry.key);
      const measured = new Map<string, number>();
      if (unsized.length > 0) {
        const values = await getMany(collection, unsized);
        unsized.forEach((k, i) => measured.set(k, values[i] === null ? 0 : offlineRecordBytes(values[i])));
      }
      return entries.map((entry) => ({ key: entry.key, bytes: entry.bytes ?? measured.get(entry.key) ?? 0 }));
    });

  return { deleteMany, entriesByParent };
}
